<script lang="ts">
  import { Settings, X } from 'lucide-svelte';
  import { fetchRoomProducts } from '$lib/api/room_product';
  import { productsQuery } from '$lib/queries/products';
  import type { ProductResponse } from '$lib/types/product';
  import Modal from './ui/Modal.svelte';
  import AddProductToRoomForm from './forms/AddProductToRoomForm.svelte';

  const products = productsQuery();

  let roomUsage = $state<Record<number, number>>({});

  async function loadRoomUsage() {
    try {
      const rps = await fetchRoomProducts();
      const usage: Record<number, number> = {};
      for (const rp of rps) {
        usage[rp.product] = (usage[rp.product] ?? 0) + rp.quantity;
      }
      roomUsage = usage;
    } catch {
      roomUsage = {};
    }
  }

  $effect(() => { void loadRoomUsage(); });

  let showDetailsDialog = $state(false);
  let showAddDialog = $state(false);

  let selectedProduct: ProductResponse | null = null;

  function openDetails(product: ProductResponse) {
    selectedProduct = product;
    showDetailsDialog = true;
  }
</script>

<div class="relative max-h-96 overflow-y-auto">
  <table class="min-w-full text-center divide-y divide-blue-100">
    <thead class="bg-blue-50">
      <tr>
        <th class="py-3 px-2 font-semibold text-text-secondary">Name</th>
        <th class="py-3 px-2 font-semibold text-text-secondary">Model</th>
        <th class="py-3 px-2 font-semibold text-text-secondary">Type</th>
        <th class="py-3 px-2 font-semibold text-text-secondary">Category</th>
        <th class="py-3 px-2 font-semibold text-text-secondary">In rooms</th>
        <th class="py-3 px-2 font-semibold text-text-secondary">Active</th>
        <th class="py-3 px-2 font-semibold text-text-secondary">Actions</th>
      </tr>
    </thead>
    <tbody class="bg-bg-canvas">
      {#each products.data ?? [] as product}
        <tr class="hover:bg-blue-50 transition">
          <td class="py-2 px-2">{product.name}</td>
          <td class="py-2 px-2">{product.model_number}</td>
          <td class="py-2 px-2">{product.type_name}</td>
          <td class="py-2 px-2">{product.category_name}</td>
          <td class="py-2 px-2">{roomUsage[product.id] ?? 0}</td>
          <td class="py-2 px-2">{product.is_active ? 'Yes' : 'No'}</td>
          <td class="py-2 px-2">
            <button
              class="flex items-center justify-center w-8 h-8 bg-bg-hover hover:bg-blue-200 rounded-full mx-auto transition"
              onclick={() => openDetails(product)}
              type="button"
              aria-label="Details"
            >
              <Settings class="w-5 h-5 text-blue-700" />
            </button>
          </td>
        </tr>
      {/each}
    </tbody>
  </table>
</div>

<Modal
  bind:open={showDetailsDialog}
  size="md"
  onclose={() => {
    if (!showAddDialog) selectedProduct = null;
  }}
>
  {#snippet header()}
    <div class="flex items-center justify-between w-full">
      <h2 class="text-xl font-semibold text-text-primary">Product Details</h2>
      <button
        type="button"
        class="p-2 bg-bg-hover hover:bg-bg-hover rounded-lg transition"
        aria-label="Close"
        onclick={() => { showDetailsDialog = false; }}
      >
        <X class="w-5 h-5" />
      </button>
    </div>
  {/snippet}
  {#snippet children()}
    {#if selectedProduct}
      <div class="space-y-2 mb-6 text-text-secondary">
        <div><span class="font-medium">Name:</span> {selectedProduct.name}</div>
        <div><span class="font-medium">Model:</span> {selectedProduct.model_number}</div>
        <div><span class="font-medium">Type:</span> {selectedProduct.type_name}</div>
        <div><span class="font-medium">Category:</span> {selectedProduct.category_name}</div>
      </div>
      <p class="text-xs text-text-secondary mb-4">
        This product is synced from ProQuote and is read-only here. Edit it in ProQuote.
      </p>
      <div class="flex justify-end gap-2 mt-4">
        <button
          class="flex items-center gap-1 px-4 py-2 bg-accent-primary hover:opacity-90 text-white rounded-lg transition"
          onclick={() => {
            showDetailsDialog = false;
            showAddDialog = true;
          }}
        >
          Add to Room
        </button>
      </div>
    {/if}
  {/snippet}
</Modal>

<Modal
  bind:open={showAddDialog}
  size="md"
  onclose={() => (showAddDialog = false)}
>
  {#snippet header()}
    <div class="flex items-center justify-between w-full">
      <h2 class="text-xl font-semibold text-text-primary">Add Product to Room</h2>
      <button
        type="button"
        class="p-2 bg-bg-hover hover:bg-bg-hover rounded-lg transition"
        aria-label="Close"
        onclick={() => { showAddDialog = false; }}
      >
        <X class="w-5 h-5" />
      </button>
    </div>
  {/snippet}
  {#snippet children()}
    {#if selectedProduct}
      <AddProductToRoomForm
        product={selectedProduct}
        onsuccess={() => {
          showAddDialog = false;
          selectedProduct = null;
        }}
        oncancel={() => (showAddDialog = false)}
      />
    {/if}
  {/snippet}
</Modal>
